import { FC } from "react";
import { isValidSubMenu } from "@utils/app.util";
import { Button } from "@components/button";
import { CustomTooltip } from "@components/tool-tip";
import { DownArrow, LeftIcon, RightIcon, UpArrow } from "@components/icons";
import { AppMenuSchemaType } from "@dialogs/app-add-edit-dialog/app-add-edit-form.schema";
import classNames from "classnames";
import { useAppLive } from "@contexts/live-app.context";
import { useIsMinimumTablet } from "@hooks/use-media-query";
// import { color } from "storybook/theming";

interface MenuProps {
  menuData: AppMenuSchemaType[];
  appId?: string;
  className?: string;
  fontColor?: string;
  bgColor?: string;
  toggleLeftMenu?: React.Dispatch<React.SetStateAction<boolean>>;
  parentMenuPath?: string;
}

export const Menu: FC<MenuProps> = ({
  menuData,
  appId,
  className,
  fontColor,
  bgColor,
  toggleLeftMenu,
  parentMenuPath,
}) => {
  const { state, isActiveRoute, onMenuClick, isAncestorMenu, dispatch } = useAppLive();
  const isMobile = !useIsMinimumTablet();
  // Only the first recursive call (a single-segment parent path) shows the
  // back button, and only on mobile where the sub-menu is a full-screen panel.
  const isFirstSubMenu = !!parentMenuPath && !parentMenuPath.includes("/");
  return (
    <ul className="top-menu">
      {isMobile && isFirstSubMenu && (
        <li className="submenu-back">
          <Button
            className="menu-btn back-btn"
            onClick={() => {
              // Collapse this sub-menu (and any of its descendants) by toggling
              // off the parent path that this recursive Menu was opened under.
              dispatch({
                type: "TOGGLE_SUBMENU",
                id: parentMenuPath,
                parentMenuPath,
                activeMenuTree: [],
              });
            }}
          >
            <LeftIcon size="15" color="#333" />
          </Button>
        </li>
      )}
      {menuData.map((menu) => {
        // unique hierarchical path
        const currentPath = parentMenuPath ? `${parentMenuPath}/${menu._id}` : `${menu._id}`;
        const isSubMenu = isValidSubMenu(menu);
        const isActive = !isSubMenu && isActiveRoute(menu._id as string);
        const isOpen = !!state.expanded[currentPath as string];
        const isSubMenuHighlighted = isSubMenu && (isOpen || isAncestorMenu(menu._id as string));
        return (
          <li
            key={menu._id}
            className={classNames(className, {
              parentMenu: isSubMenu,
              showMenu: !!parentMenuPath && isOpen,
            })}
          >
            <Button
              style={{
                backgroundColor: isActive ? bgColor : "",
                color: fontColor,
                borderRadius: "0px",
              }}
              className={classNames("menu-btn", {
                "active-menu": isActive,
                "submenu-bg": isSubMenuHighlighted,
              })}
              // variant="transparent"
              onClick={() => {
                onMenuClick({
                  menu: menu,
                  appId: appId as string,
                  parentMenuPath: currentPath,
                });
              }}
            >
              {menu.title.length > 20 ? (
                <CustomTooltip delay={2} direction="right" content={menu.title} zIndex={99999}>
                  {menu.title.slice(0, 20) + "..."}
                </CustomTooltip>
              ) : (
                menu.title
              )}

              {parentMenuPath && isSubMenu ? (
                state.expanded[currentPath] ? (
                  <UpArrow />
                ) : (
                  <DownArrow />
                )
              ) : isSubMenu ? (
                <RightIcon size="15" color={fontColor} />
              ) : (
                <></>
              )}
            </Button>

            {isSubMenu && state.expanded[currentPath] && (
              <Menu
                menuData={menu.children as AppMenuSchemaType[]}
                appId={appId}
                className="submenu"
                toggleLeftMenu={toggleLeftMenu}
                parentMenuPath={currentPath}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
};
